import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";

import { useAppSelector } from "../redux/hook";
import {
  useLazyGetAdminNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useLazyGetOwnerNotificationsQuery,
  useMarkAllOwnerNotificationsReadMutation,
  useLazyGetInvestorNotificationsQuery,
  useMarkAllInvestorNotificationsReadMutation,
} from "../redux/api/authApi";

function formatTimeAgo(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return `${seconds} sec ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  return date.toLocaleDateString();
}

export default function NotificationBell({ iconColor = "text.secondary" }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const userRole = useAppSelector((state) => state.auth?.user?.role);
  const normalizedRole = String(userRole || "").trim();
  const isAdmin = normalizedRole.toLowerCase() === "admin";
  const isOwner = normalizedRole === "assetOwner";
  const isInvestor = normalizedRole.toLowerCase() === "investor";
  const showNotifications = isAdmin || isOwner || isInvestor;

  const [
    triggerAdminNotifications,
    { data: adminNotifData, isFetching: adminNotifFetching },
  ] = useLazyGetAdminNotificationsQuery();
  const [
    triggerOwnerNotifications,
    { data: ownerNotifData, isFetching: ownerNotifFetching },
  ] = useLazyGetOwnerNotificationsQuery();
  const [
    triggerInvestorNotifications,
    { data: investorNotifData, isFetching: investorNotifFetching },
  ] = useLazyGetInvestorNotificationsQuery();
  const [markAllAdminNotificationsRead] = useMarkAllNotificationsReadMutation();
  const [markAllOwnerNotificationsRead] =
    useMarkAllOwnerNotificationsReadMutation();
  const [markAllInvestorNotificationsRead] =
    useMarkAllInvestorNotificationsReadMutation();

  let triggerNotifications = triggerOwnerNotifications;
  let markAllNotificationsRead = markAllOwnerNotificationsRead;
  let notifData = ownerNotifData;
  let notifFetching = ownerNotifFetching;
  if (isAdmin) {
    triggerNotifications = triggerAdminNotifications;
    markAllNotificationsRead = markAllAdminNotificationsRead;
    notifData = adminNotifData;
    notifFetching = adminNotifFetching;
  } else if (isInvestor) {
    triggerNotifications = triggerInvestorNotifications;
    markAllNotificationsRead = markAllInvestorNotificationsRead;
    notifData = investorNotifData;
    notifFetching = investorNotifFetching;
  }

  useEffect(() => {
    if (!showNotifications) return undefined;
    triggerNotifications({ page: 1, limit: 10, cached: false });
    const interval = setInterval(() => {
      triggerNotifications({ page: 1, limit: 10, cached: true });
    }, 5000);
    return () => clearInterval(interval);
  }, [showNotifications, triggerNotifications]);

  const notifications = notifData?.data?.notifications ?? [];
  const unreadCount = Number(notifData?.data?.unreadNotifications) || 0;

  const handleClick = async () => {
    setOpen(true);
    if (!showNotifications) return;
    try {
      await markAllNotificationsRead().unwrap();
    } catch {
      // Best-effort: still refresh the list even if mark-all fails.
    }
    triggerNotifications({ page: 1, limit: 10, cached: false });
  };

  if (!showNotifications) return null;

  return (
    <>
      <IconButton
        ref={anchorRef}
        aria-label="notifications"
        size="small"
        onClick={handleClick}
        sx={{ color: iconColor }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          overlap="circular"
        >
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,.12)",
            mt: 1,
            width: 360,
            maxWidth: "90vw",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, pt: 2, pb: 1 }}
        >
          <Box>
            <Typography fontWeight={700}>Notifications</Typography>
            <Typography variant="caption" color="text.secondary">
              {unreadCount} unread
            </Typography>
          </Box>
          {notifFetching && <CircularProgress size={16} />}
        </Stack>
        <Divider />
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List
            dense
            disablePadding
            sx={{ maxHeight: 380, overflowY: "auto" }}
          >
            {notifications.map((n) => (
              <ListItem
                key={n._id}
                alignItems="flex-start"
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: n.isRead ? "transparent" : "action.hover",
                }}
                secondaryAction={
                  !n.isRead ? (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                      }}
                    />
                  ) : null
                }
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "primary.light",
                      color: "primary.main",
                    }}
                  >
                    <NotificationsNoneIcon fontSize="small" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography fontWeight={600} fontSize={14}>
                      {n.title}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: 12 }}
                      >
                        {n.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {formatTimeAgo(n.createdAt)}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}
